import React, { useState, useEffect, useMemo, useRef } from "react";

/**
 * Three-select birthday picker (Month · Day · Year).
 * Emits ISO YYYY-MM-DD when all three are chosen. Uses LOCAL state for
 * each part so partial selections are remembered between picks (a controlled
 * value-derived design creates a state trap: emitting "" on incomplete picks
 * causes the parent value to overwrite in-progress selections).
 */
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const pad = (n) => String(n).padStart(2, "0");

export default function BirthdayPicker({ value, onChange, testidPrefix = "bday" }) {
  const [m, setM] = useState("");
  const [d, setD] = useState("");
  const [y, setY] = useState("");
  const hydrated = useRef(false);

  // Hydrate from parent `value` when it becomes a valid ISO string. We do NOT
  // clobber local state when parent value is "" — that empty is often our own
  // "incomplete" emit bouncing back and would wipe the user's in-progress picks.
  useEffect(() => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "");
    if (match) {
      const [, ny, nm, nd] = match;
      if (ny !== y || nm !== m || nd !== d) {
        setY(ny); setM(nm); setD(nd);
      }
      hydrated.current = true;
    } else if (!hydrated.current && (y || m || d)) {
      // First render with an empty value from parent — leave locals alone.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Emit to parent whenever the picks change. Only send a real ISO date when
  // all three are chosen; clear only if the user backs out of a previously-
  // complete selection.
  const wasComplete = useRef(false);
  useEffect(() => {
    if (y && m && d) {
      const max = new Date(parseInt(y, 10), parseInt(m, 10), 0).getDate();
      const day = Math.min(parseInt(d, 10), max);
      onChange(`${y}-${m}-${pad(day)}`);
      wasComplete.current = true;
    } else if (wasComplete.current) {
      onChange("");
      wasComplete.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [y, m, d]);

  const thisYear = new Date().getFullYear();
  const years = useMemo(() => {
    const arr = [];
    // Age range: 5 to 105. Recent years first so common picks are at the top.
    for (let yr = thisYear - 5; yr >= thisYear - 105; yr--) arr.push(String(yr));
    return arr;
  }, [thisYear]);

  // Day count adapts to the selected month/year.
  const daysInMonth = useMemo(() => {
    const yi = parseInt(y, 10);
    const mi = parseInt(m, 10);
    if (!yi || !mi) return 31;
    return new Date(yi, mi, 0).getDate();
  }, [y, m]);

  return (
    <div className="grid grid-cols-3 gap-2" data-testid={testidPrefix}>
      <select value={m} onChange={(e) => setM(e.target.value)} data-testid={`${testidPrefix}-month`}
              className="w-full bg-black border border-white/10 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--cl-primary)]">
        <option value="">Month</option>
        {MONTHS.map((label, i) => <option key={i} value={pad(i + 1)}>{label}</option>)}
      </select>
      <select value={d} onChange={(e) => setD(e.target.value)} data-testid={`${testidPrefix}-day`}
              className="w-full bg-black border border-white/10 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--cl-primary)]">
        <option value="">Day</option>
        {Array.from({ length: daysInMonth }, (_, i) => (
          <option key={i} value={pad(i + 1)}>{i + 1}</option>
        ))}
      </select>
      <select value={y} onChange={(e) => setY(e.target.value)} data-testid={`${testidPrefix}-year`}
              className="w-full bg-black border border-white/10 rounded px-3 py-2 text-sm focus:outline-none focus:border-[var(--cl-primary)]">
        <option value="">Year</option>
        {years.map((yr) => <option key={yr} value={yr}>{yr}</option>)}
      </select>
    </div>
  );
}
